import React, { memo, useState } from 'react';
import './base.css';

/**
 * Логотип канала на подложке фиксированного размера.
 *
 * Логотипы у провайдеров любой формы — широкие, квадратные, с полями и без, —
 * и без общей подложки ряд «прыгал» бы. Подложка тихая, на тон светлее
 * карточки. Логотипа нет или он не отдался — на его месте инициалы канала,
 * а не пустой квадрат и не «битая картинка».
 */
export const ChannelLogo = memo(function ChannelLogo({
  url,
  name,
  className,
}: {
  url: string | null;
  name: string;
  className?: string;
}): React.JSX.Element {
  const [broken, setBroken] = useState<string | null>(null);
  const showImage = url !== null && broken !== url;
  return (
    <span className={className === undefined ? 'tv-logo' : `tv-logo ${className}`} aria-hidden="true">
      {showImage ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setBroken(url)}
        />
      ) : (
        <span className="tv-logo-initials">{initials(name)}</span>
      )}
    </span>
  );
});

/** Две буквы названия: «Первый канал» → «ПК», «НТВ HD» → «НТ». */
export function initials(name: string): string {
  const words = name
    .replace(/\b(HD|FHD|UHD|4K|SD|\+\d+)\b/gi, '')
    .split(/[\s\-–—.]+/)
    .filter((word) => word !== '');
  if (words.length === 0) return '·';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** Звезда избранного: контур или заливка. */
export function StarGlyph({ filled, className }: { filled: boolean; className?: string }): React.JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    >
      <path d="m12 3.6 2.6 5.3 5.8.85-4.2 4.1 1 5.8L12 16.9l-5.2 2.75 1-5.8-4.2-4.1 5.8-.85z" />
    </svg>
  );
}

/** Значок «список каналов» для кнопки плеера. */
export function ChannelsGlyph({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M14.5 5v14M17 9h1.5M17 12h1.5M17 15h1.5" />
    </svg>
  );
}

/**
 * Полоса эфира: сколько передачи прошло. Тонкая, заливка — масштабом, а не
 * шириной: смена доли не пересчитывает раскладку.
 */
export function AirBar({ share, className }: { share: number | null; className?: string }): React.JSX.Element {
  return (
    <span className={className === undefined ? 'tv-air' : `tv-air ${className}`} aria-hidden="true">
      {share !== null && <span className="tv-air-fill" style={{ transform: `scaleX(${share})` }} />}
    </span>
  );
}
